import { jsPDF } from "jspdf";
import { LOGO_URL } from "./constants";

// -------- Logo pre-loader (fetch → base64) --------
let _logoDataUrl = null;
let _logoPromise = null;
const _loadLogo = () => {
  if (_logoDataUrl) return Promise.resolve(_logoDataUrl);
  if (_logoPromise) return _logoPromise;
  _logoPromise = fetch(LOGO_URL)
    .then((r) => r.blob())
    .then(
      (b) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => {
            _logoDataUrl = fr.result;
            resolve(_logoDataUrl);
          };
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(b);
        })
    )
    .catch(() => null);
  return _logoPromise;
};
// Kick off loader on module init so it's ready when needed.
_loadLogo();

// -------- Helpers --------

// Draw the logo respecting its intrinsic aspect ratio (fit inside maxW × maxH box).
const _drawLogo = (doc, x, y, maxW, maxH) => {
  if (!_logoDataUrl) return { w: 0, h: 0 };
  try {
    const props = doc.getImageProperties(_logoDataUrl);
    const ratio = props.width / props.height;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }
    // Center the actual image inside the maxW × maxH box.
    const dx = x + (maxW - w) / 2;
    const dy = y + (maxH - h) / 2;
    doc.addImage(_logoDataUrl, "PNG", dx, dy, w, h);
    return { w, h };
  } catch (_e) {
    return { w: 0, h: 0 };
  }
};

const _drawHeader = (doc, title) => {
  const W = doc.internal.pageSize.getWidth();
  // Bigger centered logo — no accompanying university/city text.
  const boxW = 130;
  const boxH = 100;
  _drawLogo(doc, (W - boxW) / 2, 28, boxW, boxH);
  // Title sits below the logo.
  doc.setFont("times", "bold");
  doc.setFontSize(14);
  doc.text(title, W / 2, 150, { align: "center" });
};


// Render a "template" — an array of segments where each segment is
//   { t: "text", bold?: true } or { v: "<value>" } (values are always bold).
// Wraps to page width and advances y. Returns new y.
const _writeSegments = (doc, segments, x, y, maxWidth, {
  fontSize = 11,
  lineHeight = 15,
  gap = 6,
} = {}) => {
  doc.setFontSize(fontSize);
  // Flatten segments into words with per-word style, then greedy-wrap.
  const words = [];
  segments.forEach((seg) => {
    const text = seg.v !== undefined ? String(seg.v || "") : String(seg.t || "");
    const bold = seg.v !== undefined || !!seg.bold; // values are always bold
    // Preserve explicit line breaks
    text.split(/(\s+)/).forEach((piece) => {
      if (piece.length === 0) return;
      if (/^\s+$/.test(piece)) {
        words.push({ w: " ", bold, isSpace: true });
      } else {
        words.push({ w: piece, bold, isSpace: false });
      }
    });
  });

  let cx = x;
  let cy = y;
  const spaceW = (bold) => {
    doc.setFont("times", bold ? "bold" : "normal");
    return doc.getTextWidth(" ");
  };

  for (const word of words) {
    doc.setFont("times", word.bold ? "bold" : "normal");
    const w = doc.getTextWidth(word.w);
    if (!word.isSpace && cx + w > x + maxWidth) {
      cy += lineHeight;
      cx = x;
      if (word.isSpace) continue;
    }
    if (word.isSpace) {
      // Skip trailing space at line start.
      if (cx === x) continue;
      cx += spaceW(word.bold);
      continue;
    }
    doc.text(word.w, cx, cy);
    cx += w;
  }
  // Advance past the last drawn line: baseline of last line + one lineHeight
  // (moves to next line's baseline) + caller-controlled gap.
  return cy + lineHeight + gap;
};

// Convenience: a plain "template + params" line renderer.
// `parts` mixes strings (normal weight) and { v } objects (bold values).
const _line = (doc, parts, x, y, maxWidth, opts) => {
  const segs = parts.map((p) => (typeof p === "string" ? { t: p } : p));
  return _writeSegments(doc, segs, x, y, maxWidth, opts);
};

// Simple bold-value cell like "Name : <value>" — value in bold.
const _labelValue = (doc, label, value, x, y, {
  fontSize = 11,
  bold = false,
} = {}) => {
  doc.setFontSize(fontSize);
  doc.setFont("times", bold ? "bold" : "normal");
  doc.text(label, x, y);
  const labelW = doc.getTextWidth(label);
  doc.setFont("times", "bold");
  doc.text(String(value || ""), x + labelW, y);
};

const _ordinalSuffix = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Parse "YYYY-MM-DD" as a local calendar date (not UTC midnight) so IST
// formatting doesn't shift the day on the generated / Leegality PDF.
const _parseISODateLocal = (iso) => {
  if (!iso) return null;
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Decompose an ISO date into the "day / month / year" parts used by the
// affidavit's "Declared this ___ day of ___ month of ___ year ___" phrasing.
const _declaredDateParts = (iso) => {
  const d = _parseISODateLocal(iso);
  if (!d) return { day: "____", month: "____", year: "____" };
  return {
    day: _ordinalSuffix(d.getDate()),
    month: d.toLocaleString("en-US", { month: "long" }),
    year: String(d.getFullYear()),
  };
};

const _formatDate = (iso) => {
  const d = _parseISODateLocal(iso);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

// -------- Anti-Drug Affidavit (Student + Parent) --------
// Wording below is transcribed verbatim from the official Telangana-format
// "Anti-Drug Affidavit" document — do not paraphrase, only substitute the
// bracketed dynamic fields, so the printed copy matches the official
// document exactly. Admission year is fixed at 2026 (not user-editable).
// "son/daughter of Mr./Mrs." is derived from the candidate's own gender and
// parent1's relation (Father/Mother) rather than shown as a raw slash form.
const _parentSalutation = (relation) => {
  const r = (relation || "").trim().toLowerCase();
  if (r === "father") return "Mr.";
  if (r === "mother") return "Mrs.";
  return "Mr./Mrs.";
};

export const buildAntiDrugAnnexurePdf = ({
  studentName = "", parent1Name = "", address = "", phoneNumber = "",
  course = "", studentGender = "", parent1Relation = "",
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 54; const RIGHT = W - M; const MAX = RIGHT - M;
  const relationWord = _genderWords(studentGender).child; // "son" | "daughter" | "son/daughter"
  const salutation = _parentSalutation(parent1Relation);
  _drawHeader(doc, "ANTI-DRUG AFFIDAVIT");
  let y = 160;

  doc.setFont("times", "italic"); doc.setFontSize(10);
  doc.text("(For Students Against Drug Use and Possession in Telangana)", W / 2, y, { align: "center" });
  y += 26;

  y = _line(doc, [
    "As per the guidelines and instructions provided by the Government of Telangana, Mahindra University follows the following pointers. All its stakeholders must abide by them.",
  ], M, y, MAX, { gap: 12 });

  y = _line(doc, [
    "I ", { v: studentName || "____________________" }, ` (name) ${relationWord} of ${salutation} `,
    { v: parent1Name || "____________________" }, " (name), Address: ",
    { v: address || "____________________" }, ", Phone No: ",
    { v: phoneNumber || "____________________" }, " admitted to ",
    { v: course }, ", 2026 in Mahindra University hereby agree to the following terms:",
  ], M, y, MAX, { gap: 16 });

  doc.setFont("times", "bold"); doc.setFontSize(11);
  doc.text("Declaration", W / 2, y, { align: "center" }); y += 18;
  y = _line(doc, ["I, the undersigned, hereby solemnly affirm and declare as follows:"], M, y, MAX, { gap: 10 });

  const declarationPoints = [
    "I am aware of the UGC regulations on curbing the menace of drug abuse in Higher Educational institutions. I have carefully read and fully understood the provisions contained in the said Regulations.",
    "I am aware that the possession, use, sale and distribution of alcohol/tobacco/any psychoactive substances are wrong and harmful. I acknowledge that consumption, possession, or trafficking of narcotic drugs and psychotropic substances is a criminal offence under the NDPS Act, 1985, punishable with rigorous imprisonment and fine.",
    "I am aware that ignorance of the law is no excuse in the eyes of the law. I am aware of the provisions of the NDPS Act, 1985 and other acts preventing the abuse or possession of drugs. I will also update myself with the various laws, regulations, orders, etc. issued by the concerned authorities and Government from time to time.",
    "I shall refrain from using, being under the influence of, possessing, furnishing, distributing, selling or conspiring to sell or possess, or being in the chain of sale or distribution of alcohol/tobacco/any psychoactive substances within/outside the premises of the institute/university or during any sponsored activities by the institute/university.",
    "I shall report to the authorities of the institution any irregular behaviour that I observe in relation to the possession, use, sale and distribution of alcohol/tobacco/any psychoactive substances which may have occurred at/outside the institution or during any activities conducted by any students or the institution.",
    "I shall support and actively participate in any education programmes which may be organized regarding prevention of substance use by the institution/government which would enable me to be a better student and citizen of India.",
    "I shall co-operate with the authorities of the institution and other relevant authorities in their investigation of any substance-related incident of which I may have information, and to prevent the possession, use, sale and distribution of any psychoactive substances in or around or outside my institution. I shall cooperate with Anti-Drug Committees and awareness programs conducted in my institution.",
    "I shall participate in annual medical check-ups as and when organized by the Institution. This medical check-up may include screening for drug abuse.",
    "In case my name appears as an accused in any case regarding the abuse of NDPS drugs, my parents or guardians will be informed, and the college administration will take me for drug screening to a government-approved facility.",
    "If the drug screening test results are positive, I will be referred for counselling, rehabilitation and recovery to a government de-addiction facility.",
    "If I am found in possession of drugs or consuming drugs or encouraging others to consume drugs or involved in drug-related activities in the institution, strict disciplinary and legal action may be taken against me.",
  ];
  doc.setFont("times", "normal"); doc.setFontSize(10);
  declarationPoints.forEach((c, i) => {
    const lines = doc.splitTextToSize(`${i + 1}. ${c}`, MAX);
    if (y + lines.length * 13 > 760) { doc.addPage(); y = 60; }
    doc.text(lines, M, y); y += lines.length * 13 + 6;
  });

  doc.addPage();
  y = 60;
  doc.setFont("times", "bold"); doc.setFontSize(11);
  doc.text("Consequences of Violation", W / 2, y, { align: "center" }); y += 20;

  const consequencePoints = [
    "I understand that violation of this undertaking will result in disciplinary action, including suspension, expulsion, or cancellation of admission by the institution.",
    "I am aware that in addition to the above, legal prosecution under the NDPS Act and relevant Government Orders will follow any violation.",
    "I declare that my parents/guardians are aware of this undertaking and support my compliance with anti-drug laws and institutional policies.",
  ];
  doc.setFont("times", "normal"); doc.setFontSize(10);
  consequencePoints.forEach((c, i) => {
    const lines = doc.splitTextToSize(`${i + 12}. ${c}`, MAX);
    doc.text(lines, M, y); y += lines.length * 13 + 6;
  });
  y += 10;

  doc.setFont("times", "bold"); doc.setFontSize(11);
  doc.text("Affirmation", W / 2, y, { align: "center" }); y += 18;
  y = _line(doc, [
    "I hereby affirm that the above declaration is true and correct to the best of my knowledge and belief. I understand that this affidavit shall remain legally binding throughout my period of study in the institution.",
  ], M, y, MAX, { gap: 24 });

  doc.setFont("times", "normal"); doc.setFontSize(11);
  doc.text("Signature & Name of Student:", M, y); y += 36;
  doc.text(studentName || "", M, y); y += 34;
  doc.text("Signature & Name of Parent/Guardian:", M, y); y += 36;
  doc.text(parent1Name || "", M, y); y += 34;
  doc.text("Date & Place: ______________________________", M, y); y += 40;

  // Verification is completed by the Head of the Institution, not the
  // candidate — always left blank, no auto-filled or user-editable value.
  doc.setFont("times", "bold"); doc.setFontSize(11);
  doc.text("Verification", W / 2, y, { align: "center" }); y += 18;
  doc.setFont("times", "normal");
  y = _line(doc, [
    "Subscribed and sworn before me on this _____ day of ________________ 20___ at _______________________________.",
  ], M, y, MAX, { gap: 30 });
  doc.text("Seal & Signature of Head of the Institution / Authorized signatory:", M, y);

  const blob = doc.output("blob");
  return { blob, url: URL.createObjectURL(blob), fileName: `anti-drug-affidavit-${(studentName || "candidate").replace(/\s+/g, "-")}.pdf` };
};

// -------- Anti-Drug Policy Acknowledgement --------
export const buildAntiDrugPolicyPdf = ({
  studentName = "", rollNo = "", course = "", place = "Hyderabad", signedOn = "",
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 54; const RIGHT = W - M; const MAX = RIGHT - M;
  _drawHeader(doc, "ANTI DRUG / SUBSTANCE / ALCOHOL USE POLICY");
  let y = 178;
  const clauses = [
    "Mahindra University is committed to creating and maintaining a campus environment that is free from alcohol and substance use.",
    "The University takes a serious view of alcohol and substance/drug abuse on its campus, and strongly believes that the best way to maintain an appropriate campus environment is to keep the campus alcohol and substance/drug free.",
    "The use, possession or distribution of a 'substance' is banned on the campus, in its facilities, and in the functions and programs organized / conducted by the University or its institutions.",
    "The University will take disciplinary action, as per the procedures set out in the policy, against any student found to consume, possess, sell, purchase, transport or distribute any narcotic drug or psychotropic substance / alcohol.",
    "As per the Indian Narcotic Drugs and Psychotropic Substances Act, it is illegal to produce / manufacture / cultivate, possess, sell, purchase, transport, store and/or consume any narcotic drug or psychotropic substance.",
    "The term Substance / Drugs may be any material suspected to have a mind-altering effect on the user, administered orally, inhaled, sniffed, taken intravenously or smoked.",
    "The University views the use / abuse of alcohol and other drugs as a matter of both personal and community wellness and treats any such behaviour with decisive interventions and enforcements.",
    "If any student is found on campus under the influence / in possession of alcohol or illegal / banned drugs or substances, the matter shall be regarded as an act of indiscipline.",
    "Upon finding evidence of unlawful possession, use or distribution of substance / drugs / alcohol on its premises by any student, the University will take appropriate disciplinary action, including — but not limited to — probation, suspension or expulsion.",
    "Using regularly established procedures, the University will take disciplinary action — up to and including discharge — against any student found unlawfully using, possessing or distributing 'substance' / drugs / alcohol on the University campus.",
    "Students are urged to be mindful that they remain subject to all Government laws related to the use, possession and distribution of substance / drugs / alcohol, whether on or off campus, and the University reserves the right to involve local law enforcement agencies as the situation warrants.",
  ];
  doc.setFont("times", "normal"); doc.setFontSize(10);
  clauses.forEach((c, i) => {
    const lines = doc.splitTextToSize(`${i + 1}. ${c}`, MAX);
    doc.text(lines, M, y); y += lines.length * 13 + 4;
  });
  y += 14;
  doc.setFont("times", "bold"); doc.setFontSize(11);
  doc.text("ACKNOWLEDGEMENT", M, y); y += 18;

  y = _line(doc, [
    "I, ", { v: studentName || "____________________" },
    ...(rollNo ? [" (Roll No. ", { v: rollNo }, ")"] : []),
    ", currently pursuing ", { v: course },
    " at Mahindra University, Hyderabad, hereby acknowledge that I have received, read and fully understood the above Anti-Drug / Substance / Alcohol Use Policy and I agree to abide by the same.",
  ], M, y, MAX, { gap: 18 });

  _labelValue(doc, "Place : ", place || "", M, y);
  _labelValue(doc, "Date : ", signedOn || "", M + 260, y); y += 44;
  doc.setFont("times", "normal");
  doc.text("Signature of the Student : ______________________________", M, y); y += 22;
  _labelValue(doc, "Name : ", studentName || "", M, y);

  const blob = doc.output("blob");
  return { blob, url: URL.createObjectURL(blob), fileName: `anti-drug-policy-${(studentName || "candidate").replace(/\s+/g, "-")}.pdf` };
};

// -------- High-Risk Consent (Both Parents) --------
// Wording below is transcribed verbatim from the university's original
// signed template — do not paraphrase, only substitute the bracketed
// dynamic fields, so the printed copy matches the official document exactly.
// Gender-appropriate wording for the High-Risk Consent's "son/daughter",
// "Mr./Ms." and "his/her" — falls back to the slash form when gender isn't set.
const _genderWords = (gender) => {
  const g = (gender || "").trim().toLowerCase();
  if (g === "male") return { child: "son", title: "Mr.", pronoun: "his" };
  if (g === "female") return { child: "daughter", title: "Ms.", pronoun: "her" };
  return { child: "son/daughter", title: "Mr./Ms.", pronoun: "his/her" };
};

export const buildHighRiskConsentPdf = ({
  studentName = "", studentAge = "", studentGender = "", course = "",
  parent1Name = "", parent2Name = "",
  medicalIllness = "",
  parent1Phone = "", parent2Phone = "",
  declaredDate = "",
}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 54; const RIGHT = W - M; const MAX = RIGHT - M;
  _drawHeader(doc, "HIGH-RISK CONSENT");
  let y = 178;

  const hasIllness = medicalIllness && medicalIllness.toLowerCase() !== "none";
  const dateStr = _formatDate(declaredDate);
  // Parent 2 is optional in Candidate Details — only show a second column
  // (name/sign/phone/date) if one was actually provided.
  const hasParent2 = !!(parent2Name || "").trim();
  const { child, title, pronoun } = _genderWords(studentGender);

  y = _line(doc, [
    "I, ", { v: parent1Name || "…………………………………………………" },
    ...(hasParent2 ? [" and ", { v: parent2Name }] : []),
    ` have explained about our ${child}'s condition. ${title} `,
    { v: studentName || "________________" }, ", ", { v: studentAge || "__" },
    " years old ", { v: studentGender || "male/female" }, ", studying ",
    { v: course }, " (Program) at MAHINDRA UNIVERSITY who has been diagnosed with ",
    hasIllness ? { v: medicalIllness } : "…………………………………………",
    `, a medical illness. We give full consent to be residents of the university campus. We don't hold the management {or} medical personnel responsible if any untoward event happens like worsening of ${pronoun} condition or over dosage of the prescribed drugs by the family doctor. In case the situation is not resolved and further treatment is required we will take our ward home or as required depending on Mahindra University doctors' advice.`,
  ], M, y, MAX, { gap: 20 });

  const col2 = M + MAX / 2 + 10;
  doc.setFont("times", "normal");
  _labelValue(doc, "PARENT 1 NAME: ", parent1Name || "", M, y);
  if (hasParent2) _labelValue(doc, "PARENT 2 NAME: ", parent2Name || "", col2, y);
  y += 46;
  doc.text("PARENT 1 SIGN: ", M, y);
  if (hasParent2) doc.text("PARENT 2 SIGN: ", col2, y);
  y += 32;
  _labelValue(doc, "PHONE NUMBER: ", parent1Phone || "", M, y);
  if (hasParent2) _labelValue(doc, "PHONE NUMBER: ", parent2Phone || "", col2, y);
  y += 28;
  _labelValue(doc, "DATE: ", dateStr, M, y);
  if (hasParent2) _labelValue(doc, "DATE: ", dateStr, col2, y);

  const blob = doc.output("blob");
  return { blob, url: URL.createObjectURL(blob), fileName: `high-risk-consent-${(studentName || "candidate").replace(/\s+/g, "-")}.pdf` };
};
